import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  getVehicleStockCount,
  getAvailableYearsForFilters,
  getAvailableMakesForFilters,
  getAvailableModelsForFilters,
  getAvailableTrimsForModel,
} from '@/api'
import type { VehicleData, VehicleItem, Make } from '@/types'

export const useVehiclesStore = defineStore('Vehicles', () => {
  // vehicles data
  const vehiclesInUserInventory = ref<VehicleItem[] | null>(null)
  const vehiclesData = ref<VehicleData[]>([])
  const makeCounts = ref()

  // filters data loading
  const vehicleDataLoading = ref(true)
  const yearsDataLoading = ref(true)
  const makesDataLoading = ref(true)
  const modelsDataLoading = ref(false)
  const loadingError = ref('')

  const availableYears = ref<number[] | null>(null)
  const availableMakes = ref<Make[]>([])
  const availableModels = ref<string[]>([])


  const getVehiclesData = async () => {
    vehicleDataLoading.value = true

    try {
      const response: any = await getVehicleStockCount()
      vehiclesInUserInventory.value =
        response?.facets.make_model_sidebar.buckets
      makeCounts.value = response?.facets.make_sidebar.buckets ?? []
      vehicleDataLoading.value = false

    } catch (error) {
      makeCounts.value = []
      loadingError.value = 'Error Loading Vehicles.'
      vehicleDataLoading.value = false
      console.error(error)
    }
  }

  const getAvailableYears = async () => {
    yearsDataLoading.value = true
    const response = await getAvailableYearsForFilters()
    yearsDataLoading.value = false
    return (availableYears.value = response.years)
  }

  const getAvailableMakes = async () => {
    makesDataLoading.value = true

    const years = await getAvailableYears()

    // First, get all makes responses
    const makesPromises = years.map(async (year: number) => {
      const makesResponse = await getAvailableMakesForFilters(year);
      if (makesResponse && makesResponse.year === 2024) {
        // For each make, get its models
        const makePromises = makesResponse.makes.map(async (make: string) => {
          const modelsForMake = await getAvailableModels(year, make);
          return { make, models: modelsForMake };
        });

        // Wait for all models to be fetched
        const makes = await Promise.all(makePromises)
        availableMakes.value.push(...makes)
      }
    })

    // Wait for all years to be processed
    await Promise.all(makesPromises)

    makesDataLoading.value = false
    return availableMakes.value.sort((a, b) =>
      String(a.make).localeCompare(String(b.make)),
    )
  }

  const getAvailableModels = async (year: number, make: string) => {
    modelsDataLoading.value = true
    const response = await getAvailableModelsForFilters(year, make)
    modelsDataLoading.value = false
    return response.models
  }

  const getAvailableTrims = async (
    year: number,
    make: string,
    model: string,
  ) => {
    return await getAvailableTrimsForModel(year, make, model)
  }

  return {
    //state
    vehiclesInUserInventory,
    loadingError,
    vehicleDataLoading,
    availableModels,
    yearsDataLoading,
    modelsDataLoading,
    makesDataLoading,
    availableMakes,
    makeCounts,
    vehiclesData,
    //actions
    getVehiclesData,
    getAvailableYears,
    getAvailableMakes,
    getAvailableModels,
    getAvailableTrims
  }
})
